import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SIMBY_MASCOT = '/images/simby-mascote.png';

const FloatingChat = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [hasShownWelcome, setHasShownWelcome] = useState(false);
    const [position, setPosition] = useState({ right: 24, bottom: 24 });
    const [isDragging, setIsDragging] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const dragRef = useRef({ startX: 0, startY: 0, startRight: 0, startBottom: 0 });
    const hasDragged = useRef(false);

    const onPointerDown = useCallback((e) => {
        const t = e.touches?.[0] || e;
        hasDragged.current = false;
        dragRef.current = { startX: t.clientX, startY: t.clientY, startRight: position.right, startBottom: position.bottom };
        setIsDragging(true);
    }, [position]);

    const onPointerMove = useCallback((e) => {
        const t = e.touches?.[0] || e;
        const dx = t.clientX - dragRef.current.startX;
        const dy = t.clientY - dragRef.current.startY;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) hasDragged.current = true;
        const newRight = Math.max(8, Math.min(window.innerWidth - 72, dragRef.current.startRight - dx));
        const newBottom = Math.max(8, Math.min(window.innerHeight - 72, dragRef.current.startBottom - dy));
        setPosition({ right: newRight, bottom: newBottom });
    }, []);

    const onPointerUp = useCallback(() => setIsDragging(false), []);

    useEffect(() => {
        if (!isDragging) return;
        window.addEventListener('mousemove', onPointerMove);
        window.addEventListener('mouseup', onPointerUp);
        window.addEventListener('touchmove', onPointerMove, { passive: false });
        window.addEventListener('touchend', onPointerUp);
        return () => {
            window.removeEventListener('mousemove', onPointerMove);
            window.removeEventListener('mouseup', onPointerUp);
            window.removeEventListener('touchmove', onPointerMove);
            window.removeEventListener('touchend', onPointerUp);
        };
    }, [isDragging, onPointerMove, onPointerUp]);

    // Scroll to bottom when messages change
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isTyping]);

    // Focus input when chat opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    // Show welcome message when chat opens for the first time
    useEffect(() => {
        if (isOpen && !hasShownWelcome && messages.length === 0) {
            setMessages([{
                role: 'assistant',
                content: 'Oi! Eu sou a Simby, sua amiga girafa especialista em Inteligência Relacional. Como posso te ajudar hoje? Pode me perguntar sobre Inteligência Relacional, a plataforma, ou qualquer dúvida que você tiver!'
            }]);
            setHasShownWelcome(true);
        }
    }, [isOpen, hasShownWelcome, messages.length]);

    // Handle ESC key to close
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen]);

    // Track mobile breakpoint
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSend = async () => {
        if (!input.trim() || isTyping) return;

        const userMessage = input.trim();
        setInput('');

        // Add user message
        const newMessages = [...messages, { role: 'user', content: userMessage }];
        setMessages(newMessages);
        setIsTyping(true);

        try {
            const response = await axios.post(
                `${API}/simby-chat/message`,
                {
                    messages: messages.map(m => ({ role: m.role, content: m.content })),
                    user_message: userMessage
                },
                { withCredentials: true }
            );

            setMessages([...newMessages, {
                role: 'assistant',
                content: response.data.response
            }]);
        } catch (error) {
            console.error('Error sending message:', error);
            setMessages([...newMessages, {
                role: 'assistant',
                content: 'Opa, tive um probleminha. Pode tentar de novo?'
            }]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (hidden) {
        return (
            <button
                onClick={() => setHidden(false)}
                className="fixed z-50 bg-purple-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg"
                style={{ bottom: 16, right: 16 }}
            >
                Simby 🦒
            </button>
        );
    }

    return (
        <>
            {/* Floating Button — draggável */}
            {!isOpen && (
                <div
                    className="fixed z-50"
                    style={{ right: position.right, bottom: position.bottom, touchAction: 'none' }}
                >
                    <div className="relative group">
                        <button
                            onMouseDown={onPointerDown}
                            onTouchStart={onPointerDown}
                            onClick={() => { if (!hasDragged.current) setIsOpen(true); }}
                            className="w-16 h-16 rounded-full bg-purple-600 hover:bg-purple-700 shadow-lg flex items-center justify-center select-none"
                            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                            title="Fale com a Simby"
                        >
                            <img src={SIMBY_MASCOT} alt="Simby" className="w-12 h-12 object-contain pointer-events-none" />
                        </button>
                        {/* Botão fechar — aparece no hover/focus */}
                        <button
                            onClick={(e) => { e.stopPropagation(); setHidden(true); }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-gray-600 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                            title="Fechar"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* Chat Popup — full-screen no mobile, janela flutuante no desktop */}
            <div
                className={`
                    fixed z-50 flex flex-col overflow-hidden bg-white
                    transform transition-all duration-300
                    ${isMobile
                        ? 'inset-0 rounded-none'
                        : 'w-[380px] max-w-[calc(100vw-1rem)] h-[500px] max-h-[calc(100vh-5rem)] rounded-2xl shadow-2xl origin-bottom-right'
                    }
                    ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}
                `}
                style={isMobile ? {} : { right: Math.min(position.right, window.innerWidth - 396), bottom: position.bottom + 72 }}
            >
                {/* Header */}
                <div className="bg-purple-600 text-white p-4 flex items-center gap-3 flex-shrink-0">
                    <img
                        src={SIMBY_MASCOT}
                        alt="Simby"
                        className="w-10 h-10 object-contain"
                    />
                    <div className="flex-1">
                        <h3 className="font-bold">Fale com a Simby</h3>
                        <p className="text-xs text-purple-200">Sua assistente de Inteligência Relacional</p>
                    </div>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="p-2 hover:bg-purple-500 rounded-full transition-colors"
                        aria-label="Fechar chat"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                    {messages.map((message, index) => (
                        <div
                            key={index}
                            className={`flex items-end gap-2 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            {message.role === 'assistant' && (
                                <img
                                    src={SIMBY_MASCOT}
                                    alt="Simby"
                                    className="w-8 h-8 object-contain flex-shrink-0"
                                />
                            )}
                            <div
                                className={`
                                    max-w-[75%] px-4 py-2 rounded-2xl
                                    ${message.role === 'user'
                                        ? 'bg-purple-600 text-white rounded-br-md'
                                        : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100'
                                    }
                                `}
                            >
                                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                            </div>
                        </div>
                    ))}

                    {/* Typing Indicator */}
                    {isTyping && (
                        <div className="flex items-end gap-2">
                            <img
                                src={SIMBY_MASCOT}
                                alt="Simby"
                                className="w-8 h-8 object-contain flex-shrink-0"
                            />
                            <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                                <div className="flex gap-1">
                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 bg-white border-t border-gray-200">
                    <div className="flex gap-2">
                        <textarea
                            ref={inputRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Digite sua pergunta..."
                            disabled={isTyping}
                            className="
                                flex-1 resize-none rounded-xl border border-gray-300
                                px-4 py-2 text-sm
                                focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent
                                disabled:bg-gray-100 disabled:cursor-not-allowed
                                max-h-24
                            "
                            rows={1}
                        />
                        <Button
                            onClick={handleSend}
                            disabled={!input.trim() || isTyping}
                            className="bg-purple-600 hover:bg-purple-700 rounded-xl px-4"
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </div>
                    <p className="text-xs text-gray-400 text-center mt-2">
                        Pressione Enter para enviar
                    </p>
                </div>
            </div>
        </>
    );
};

export default FloatingChat;

